import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Trash2, Plus, UtensilsCrossed, Link2 } from 'lucide-react'
import { slideInRight } from '@/lib/motion'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { useFloorPlanStore } from '@/lib/store/useFloorPlanStore'
import { useUpdateArea, useDeleteArea } from '@/lib/queries/useAreas'
import { useSoftDeleteTable, useDeleteTableCombination } from '@/lib/queries/useTables'
import type { Area } from '@/lib/types/area'
import type { TableCombination } from '@/lib/types/table'

interface AreaDetailPanelProps {
  area: Area | null
  combinations: TableCombination[]
  onClose: () => void
  onAddTable: (areaId: number) => void
}

export function AreaDetailPanel({ area, combinations, onClose, onAddTable }: AreaDetailPanelProps) {
  const { selectArea } = useFloorPlanStore()
  const updateArea = useUpdateArea()
  const deleteArea = useDeleteArea()
  const softDeleteTable = useSoftDeleteTable()
  const deleteCombination = useDeleteTableCombination()

  const [name, setName] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteTable, setDeleteTable] = useState<{ id: number; documentId: string } | null>(null)

  useEffect(() => {
    if (area) setName(area.name)
  }, [area])

  const handleClose = () => {
    selectArea(null)
    onClose()
  }

  const handleRename = () => {
    if (!area || !name.trim()) return
    updateArea.mutate({ documentId: area.documentId, name: name.trim() } as any)
  }

  const handleDeleteArea = () => {
    if (!area) return
    deleteArea.mutate({ id: area.id, documentId: area.documentId }, {
      onSuccess: () => {
        setShowDeleteConfirm(false)
        handleClose()
      },
    })
  }

  const handleDeleteTable = (t: { id: number; documentId: string }) => {
    softDeleteTable.mutate(t, {
      onSuccess: () => setDeleteTable(null),
      onError: () => setDeleteTable(null),
    })
  }

  const tables = area?.tables ?? []
  const areaCombinations = combinations.filter(
    (c) => c.tables?.some((t) => tables.some((at) => at.id === t.id)),
  )

  return (
    <>
      <AnimatePresence>
        {area && (
          <motion.div
            {...slideInRight}
            className="fixed right-0 top-0 bottom-0 w-80 bg-[var(--color-bg-surface)] border-l border-[var(--color-border-subtle)] z-50 flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border-subtle)]">
              <h3 className="font-display font-semibold text-[var(--color-text-primary)]">
                Area Details
              </h3>
              <button
                onClick={handleClose}
                className="p-1 rounded-[var(--radius-sm)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              {/* Rename */}
              <div className="flex gap-2">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Area name"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRename}
                  loading={updateArea.isPending}
                  disabled={name === area.name}
                  className="shrink-0"
                >
                  Rename
                </Button>
              </div>

              {/* Tables list */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-[var(--color-text-secondary)]">
                    Tables ({tables.length})
                  </h4>
                  <Button variant="ghost" size="icon-sm" onClick={() => onAddTable(area.id)}>
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {tables.length === 0 ? (
                  <p className="text-xs text-[var(--color-text-muted)] py-3 text-center">No tables in this area</p>
                ) : (
                  <div className="space-y-1">
                    {tables.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center gap-2 p-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)]"
                      >
                        <UtensilsCrossed className="h-3.5 w-3.5 text-[var(--color-text-muted)] shrink-0" />
                        <span className="flex-1 text-sm text-[var(--color-text-primary)] font-medium">{t.name}</span>
                        <span className="text-[10px] text-[var(--color-text-muted)]">{t.seatsMax}p</span>
                        <button
                          onClick={() => setDeleteTable({ id: t.id, documentId: t.documentId })}
                          className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-accent-red)] transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Combinations */}
              <div>
                <h4 className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">
                  Combinations ({areaCombinations.length})
                </h4>
                {areaCombinations.length === 0 ? (
                  <p className="text-xs text-[var(--color-text-muted)] py-3 text-center">
                    Drag tables near each other to combine
                  </p>
                ) : (
                  <div className="space-y-1">
                    {areaCombinations.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-2 p-2 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)]"
                      >
                        <Link2 className="h-3.5 w-3.5 text-[var(--color-accent-amber)] shrink-0" />
                        <span className="flex-1 text-sm text-[var(--color-text-primary)]">{c.name}</span>
                        <Badge>{c.seatsMin}-{c.seatsMax}p</Badge>
                        <button
                          onClick={() => deleteCombination.mutate(c.documentId)}
                          className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-accent-red)] transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Delete area */}
              <div className="border-t border-[var(--color-border-subtle)] pt-4">
                <Button
                  variant="danger"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete Area
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete area confirmation */}
      <Modal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Area?"
        size="sm"
      >
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">
          This will delete <strong>{area?.name}</strong> and all its tables. Tables with upcoming bookings cannot be deleted.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleDeleteArea}
            loading={deleteArea.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>

      {/* Delete table confirmation */}
      <Modal
        open={deleteTable !== null}
        onClose={() => setDeleteTable(null)}
        title="Delete Table?"
        size="sm"
      >
        <p className="text-sm text-[var(--color-text-secondary)] mb-4">
          Tables with upcoming bookings cannot be deleted.
        </p>
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={() => setDeleteTable(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => deleteTable && handleDeleteTable(deleteTable)}
            loading={softDeleteTable.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  )
}
